import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
    FaArrowRight, FaCheckCircle, FaChartLine, FaHistory,
    FaCamera, FaBriefcase, FaUsers, FaBuilding, FaFileSignature, FaMedal,
    FaMapMarkerAlt,
} from "react-icons/fa";
import HeaderAny from "../../components/header";
import MobileNav from "../../components/header-mobile";
import Footer from "../../components/footer";
import buildingImg from "../../assets/pub-hero-building.png";
import heroCurveImg from "../../assets/brd-hero-curve.jpg";
import logoMark from "../../assets/logo.svg";
import searchHeroImg from "../../assets/sol-search-hero.svg";
import "./solutions.css";
import "./borderless.css";
import { registerProfessional } from "../../utils/professional-api";

const GET_STARTED_URL = "https://web.conra.net";

const PROFILE_FEATURES = [
    { Icon: FaCheckCircle, title: "No Self-Certification", desc: "Every project on your profile is verified by the contractor and client." },
    { Icon: FaChartLine, title: "Reputation That Grows", desc: "Your portfolio updates automatically as projects progress, building trust and attracting better opportunities." },
    { Icon: FaHistory, title: "Complete Project Lifecycle", desc: "From start to finish, every milestone is captured, time stamped and permanently recorded." },
];

const PROFILE_STATS = [
    { value: "23", label: "Projects" },
    { value: "14", label: "Completed" },
    { value: "9", label: "Ongoing" },
];

const PROFILE_PROJECTS = [
    { name: "Ruiru Mixed-Use Development", code: "NYL1299-KEN" },
    { name: "Thika Road Offices", code: "NYL1301-KEN" },
    { name: "Kilimani Resdence", code: "NYL1300-KEN" },
];

const PROVES_CHECKLIST = [
    "Permanent proof your specifications were followed.",
    "Shared accountability between all project stakeholders.",
    "Contractor and architect earn verified credibility.",
];

const BEFORE_ITEMS = [
    { Icon: FaCamera, text: "Beautiful renders. Finished photos. No evidence of what was actually built." },
    { Icon: FaBriefcase, text: "A PDF you made yourself. No third party has verified a single claim in it." },
    { Icon: FaHistory, text: "No timeline. No milestones. No record of how the project actually went." },
    { Icon: FaUsers, text: "Disconnected from clients, contractors, and the building that exists today." },
];

const AFTER_ITEMS = [
    { Icon: FaBuilding, text: "Every building you've ever been responsible for verified, timestamped." },
    { Icon: FaFileSignature, text: "Third party verified milestones. Clients confirm, contractors confirm, the record is sealed." },
    { Icon: FaHistory, text: "The full lifecycle — from first drawing to last brick attached to your registration number." },
    { Icon: FaMedal, text: "A living professional legacy that grows with every project and outlasts any PDF." },
];

const GLOBAL_STATS = [
    { value: "72+", label: "Countries" },
    { value: "40K+", label: "Verified Projects" },
    { value: "100+", label: "Architect Profile" },
];

const MAP_PINS = [
    { country: "United Kingdom", code: "ZXC6421-UK", top: "18%", left: "40%" },
    { country: "United States", code: "NYL1099-USA", top: "42%", left: "12%" },
    { country: "China", code: "UJZ9832-CHN", top: "34%", left: "74%" },
    { country: "Kenya", code: "MOR1303-KEN", top: "58%", left: "50%" },
    { country: "Brazil", code: "HYD3200-BRA", top: "68%", left: "24%" },
    { country: "Australia", code: "LLU2999-AUS", top: "72%", left: "84%" },
];

const REGISTRY_COUNTRIES = [
    // Kenya
    ["Kenya", "🇰🇪", "BORAQS", "AQ-", "9281"],
    // East Africa
    ["Tanzania", "🇹🇿", "ARB Tanzania", "ARB-", "3041"],
    ["Uganda", "🇺🇬", "URBPA", "UG-", "2210"],
    ["Rwanda", "🇷🇼", "RHA / Rwanda", "RW-", "1183"],
    ["Ethiopia", "🇪🇹", "EIA", "ET-", "5572"],
    ["Burundi", "🇧🇮", "OAB", "BI-", "0441"],
    // Africa
    ["South Africa", "🇿🇦", "SACAP", "ARC-", "4521"],
    ["Nigeria", "🇳🇬", "ARCON", "A-", "3310"],
    ["Ghana", "🇬🇭", "ARB Ghana", "ARB-", "1099"],
    ["Egypt", "🇪🇬", "SUA / Egypt", "EG-", "8834"],
    ["Morocco", "🇲🇦", "Ordre des Architectes", "MA-", "2217"],
    // International
    ["United Kingdom", "🇬🇧", "ARB", "A-", "9281"],
    ["United States", "🇺🇸", "AIA / State Board", "AIA-", "00881"],
    ["Canada", "🇨🇦", "OAA / Provincial", "A-", "7712"],
    ["Australia", "🇦🇺", "AACA / State Board", "NSW-", "9021"],
    ["Germany", "🇩🇪", "Bundesarchitektenkammer", "BAK-", "8841"],
    ["France", "🇫🇷", "Ordre des Architectes", "FR-", "06622"],
    ["Netherlands", "🇳🇱", "Architectenregister", "BA-", "1034"],
    ["Singapore", "🇸🇬", "BOA", "A-", "2291"],
    ["China", "🇨🇳", "NABEA", "CN-", "77120"],
    ["Japan", "🇯🇵", "JAEIC", "JP-", "40012"],
    ["Brazil", "🇧🇷", "CAU/BR", "A-", "99210"],
    ["Mexico", "🇲🇽", "SEP Cédula", "MX-", "71120"],
    ["Turkey", "🇹🇷", "TMMOB", "TR-", "30021"],
    ["Indonesia", "🇮🇩", "IAI", "ID-", "11840"],
];

const REGISTRY_FEATURES = [
    {
        icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20M2 12h20" /></svg>,
        title: "Register once",
        desc: "Enter your country and board registration number a single time. No re-applying per project or per client.",
    },
    {
        icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 9h18M8 4v5" /></svg>,
        title: "One global portfolio",
        desc: "Every project you're added to builds one living portfolio — anyone can see the real work you've delivered, across every country and client.",
    },
    {
        icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15.3 15.3 0 0 1 0 20 15.3 15.3 0 0 1 0-20Z" /></svg>,
        title: "Found globally",
        desc: "Any contractor on ConRa, anywhere in the world, can look you up by name or registry ID and add you to a project in seconds.",
    },
];


const ArchitectRegistrySection = () => {
    const navigate = useNavigate();
    const [view, setView] = useState("form");
    const [role, setRole] = useState("architect");
    const [roleOpen, setRoleOpen] = useState(false);
    const [countryOpen, setCountryOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [selected, setSelected] = useState(null);
    const [nameVal, setNameVal] = useState("");
    const [regVal, setRegVal] = useState("");
    const [emailVal, setEmailVal] = useState("");
    const [phoneVal, setPhoneVal] = useState("");
    const [turnstileToken, setTurnstileToken] = useState("");
    const [doneName, setDoneName] = useState("");
    const [registryId, setRegistryId] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState("");
    const turnstileContainerRef = useRef(null);
    const turnstileWidgetId = useRef(null);

    useEffect(() => {
        const callbackName = "__turnstileReady";

        const mountWidget = () => {
            if (!turnstileContainerRef.current || turnstileWidgetId.current !== null) return;
            turnstileWidgetId.current = window.turnstile.render(turnstileContainerRef.current, {
                sitekey: "0x4AAAAAAEL_0YJSgfZT-2nV",
                callback: (token) => setTurnstileToken(token),
                "expired-callback": () => setTurnstileToken(""),
                "error-callback": () => setTurnstileToken(""),
            });
        };

        window[callbackName] = mountWidget;

        const existing = document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]');
        if (existing) {
            if (window.turnstile) mountWidget();
            else existing.addEventListener("load", mountWidget, { once: true });
        } else {
            const script = document.createElement("script");
            script.src = `https://challenges.cloudflare.com/turnstile/v0/api.js?onload=${callbackName}&render=explicit`;
            script.async = true;
            script.defer = true;
            document.body.appendChild(script);
        }

        return () => {
            delete window[callbackName];
            if (turnstileWidgetId.current !== null && window.turnstile) {
                window.turnstile.remove(turnstileWidgetId.current);
                turnstileWidgetId.current = null;
            }
        };
    }, []);

    const sel = REGISTRY_COUNTRIES.find(c => c[0] === selected);
    const filtered = REGISTRY_COUNTRIES.filter(c => {
        if (!query.trim()) return true;
        const q = query.trim().toLowerCase();
        return c[0].toLowerCase().includes(q) || c[2].toLowerCase().includes(q);
    });
    const ready = !!(sel && nameVal.trim() && regVal.trim() && emailVal.trim() && turnstileToken);
    const ROLES = [
        { key: "architect", label: "Architect", professional_type: "architect", isFirm: false, namePlaceholder: "e.g. Amara Okonkwo", nameLabel: "Full Name" },
        { key: "firm", label: "Architectural Firm", professional_type: "architectural_firm", isFirm: true, namePlaceholder: "e.g. Epitome Architects Ltd.", nameLabel: "Firm Name" },
        { key: "engineer", label: "Engineer", professional_type: "engineer", isFirm: false, namePlaceholder: "e.g. David Kariuki", nameLabel: "Full Name" },
        { key: "engineering_firm", label: "Engineering Firm", professional_type: "engineering_firm", isFirm: true, namePlaceholder: "e.g. Apex Engineers Ltd.", nameLabel: "Firm Name" },
    ];
    const currentRole = ROLES.find(r => r.key === role) || ROLES[0];
    const isFirm = currentRole.isFirm;

    useEffect(() => {
        const handleClick = (e) => {
            if (!e.target.closest("[data-country-box]")) setCountryOpen(false);
            if (!e.target.closest("[data-role-box]")) setRoleOpen(false);
        };
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, []);

    const submit = async () => {
        if (!ready || submitting) return;
        setSubmitting(true);
        setSubmitError("");
        try {
            const data = await registerProfessional({
                name: nameVal.trim(),
                email: emailVal.trim(),
                registration_no: regVal,
                professional_type: currentRole.professional_type,
                area_of_operation: sel[0],
                turnstile_token: turnstileToken,
            });
            setDoneName(nameVal.trim());
            setRegistryId(data?.registry_id || data?.id || `${sel[0].slice(0, 3).toUpperCase()}-${regVal.trim()}`);
            setView("done");
        } catch (err) {
            setTurnstileToken("");
            if (turnstileWidgetId.current !== null && window.turnstile) {
                window.turnstile.reset(turnstileWidgetId.current);
            }
            setSubmitError(err.message || "Registration failed. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const reset = () => {
        setView("form");
        setSelected(null);
        setNameVal("");
        setRegVal("");
        setEmailVal("");
        setPhoneVal("");
        setQuery("");
        setDoneName("");
        setRegistryId("");
        setTurnstileToken("");
        if (turnstileWidgetId.current !== null && window.turnstile) {
            window.turnstile.reset(turnstileWidgetId.current);
        }
        setSubmitError("");
    };

    return (
        <section id="register" className="brd-reg-section">
            <div className="brd-reg-inner">
                {/* Left panel */}
                <div className="brd-reg-left">
                    <span className="brd-reg-badge">
                        <span className="brd-reg-badge-dot" />
                        Borderless · Your Global Portfolio
                    </span>
                    <h2 className="brd-reg-h2">
                        Every project<br />finds your <span className="brd-highlight">portfolio.</span>
                    </h2>
                    <div className="brd-reg-features">
                        {REGISTRY_FEATURES.map(({ icon, title, desc }) => (
                            <div key={title} className="brd-reg-feature">
                                <span className="brd-reg-feature-icon">{icon}</span>
                                <div>
                                    <h3 className="brd-reg-feature-title">{title}</h3>
                                    <p className="brd-reg-feature-desc">{desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Right panel — form card */}
                <div className="brd-reg-right-wrap">
                    <div className="brd-reg-sample-link-wrap">
                        <a href="/solutions/borderless/portfolio" className="brd-reg-sample-link">
                            Sample portfolio
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M7 17 17 7M7 7h10v10" />
                            </svg>
                        </a>
                    </div>
                    <div className="brd-reg-card">
                        {view === "form" && (
                            <div>
                                <div className="brd-reg-form-header">
                                    <span className="brd-reg-form-icon">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M19 8v6M22 11h-6" />
                                        </svg>
                                    </span>
                                    <h3 className="brd-reg-form-title">Join the Professional Registry</h3>
                                </div>
                                <p className="brd-reg-form-sub">Free for individual professionals. Takes about 2 minutes.</p>

                                {/* Registering as */}
                                <label className="brd-reg-label">Registering as</label>
                                <div data-role-box="" className="brd-reg-dropdown-wrap">
                                    <button className="brd-reg-dropdown-btn" onClick={() => { setRoleOpen(o => !o); setCountryOpen(false); }}>
                                        <span className="brd-reg-dropdown-value">{currentRole.label}</span>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0f1125" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                            style={{ transform: roleOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform .18s" }}>
                                            <path d="m6 9 6 6 6-6" />
                                        </svg>
                                    </button>
                                    {roleOpen && (
                                        <div className="brd-reg-dropdown-menu">
                                            {ROLES.map(opt => (
                                                <button key={opt.key} className="brd-reg-dropdown-item" onClick={() => { setRole(opt.key); setRoleOpen(false); }}>
                                                    <span style={{ flex: 1 }}>{opt.label}</span>
                                                    {role === opt.key && (
                                                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M20 6 9 17l-5-5" />
                                                        </svg>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Country */}
                                <label className="brd-reg-label">Country of Registration</label>
                                <div data-country-box="" className="brd-reg-dropdown-wrap">
                                    <button className="brd-reg-dropdown-btn" onClick={() => { setCountryOpen(o => !o); setRoleOpen(false); setQuery(""); }}>
                                        {sel ? (
                                            <span className="brd-reg-dropdown-value brd-reg-country-selected">
                                                <span className="brd-reg-flag">{sel[1]}</span>{sel[0]}
                                            </span>
                                        ) : (
                                            <span className="brd-reg-dropdown-placeholder">Select country....</span>
                                        )}
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0f1125" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                            style={{ transform: countryOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform .18s" }}>
                                            <path d="m6 9 6 6 6-6" />
                                        </svg>
                                    </button>
                                    {countryOpen && (
                                        <div className="brd-reg-dropdown-menu">
                                            <div className="brd-reg-search-wrap">
                                                <div className="brd-reg-search-inner">
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round">
                                                        <circle cx="11" cy="11" r="7" /><path d="m20 20-3-3" />
                                                    </svg>
                                                    <input
                                                        className="brd-reg-search-input"
                                                        placeholder="Search country"
                                                        value={query}
                                                        onChange={e => setQuery(e.target.value)}
                                                        autoFocus
                                                    />
                                                </div>
                                            </div>
                                            <div className="brd-reg-dropdown-list">
                                                {filtered.length === 0 ? (
                                                    <div className="brd-reg-dropdown-empty">No countries found</div>
                                                ) : filtered.map(c => (
                                                    <button key={c[0]} className="brd-reg-dropdown-item" onClick={() => { setSelected(c[0]); setCountryOpen(false); setQuery(""); }}>
                                                        <span className="brd-reg-flag">{c[1]}</span>
                                                        <span style={{ flex: 1 }}>{c[0]}</span>
                                                        <span className="brd-reg-board-tag">{c[2]}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {sel && (
                                        <p className="brd-reg-country-hint">
                                            Verified against <strong className="brd-reg-country-hint-board">{sel[2]}</strong>
                                        </p>
                                    )}
                                </div>

                                {/* Name */}
                                <label className="brd-reg-label">{currentRole.nameLabel}</label>
                                <input
                                    className="brd-reg-input"
                                    placeholder={currentRole.namePlaceholder}
                                    value={nameVal}
                                    onChange={e => setNameVal(e.target.value)}
                                />

                                {/* Registration number */}
                                <label className="brd-reg-label">Registration No.</label>
                                <input
                                    className="brd-reg-input"
                                    placeholder={
                                        !sel
                                            ? "Select a country first"
                                            : selected === "Kenya" && role === "architect"
                                                ? "e.g. A9999"
                                                : "e.g. 999A"
                                    }
                                    value={regVal}
                                    onChange={e => setRegVal(e.target.value)}
                                />

                                {/* Email */}
                                <label className="brd-reg-label">Email Address</label>
                                <input
                                    className="brd-reg-input"
                                    placeholder="you@firm.com"
                                    type="email"
                                    value={emailVal}
                                    onChange={e => setEmailVal(e.target.value)}
                                />

                                {/* Phone number — commented out */}
                                {false && (
                                    <>
                                        <label className="brd-reg-label">Phone Number</label>
                                        <input
                                            className="brd-reg-input brd-reg-input--last"
                                            placeholder="e.g. +254 700 000 000"
                                            type="tel"
                                            value={phoneVal}
                                            onChange={e => setPhoneVal(e.target.value)}
                                        />
                                    </>
                                )}

                                <div className="brd-reg-turnstile-wrap">
                                    <div ref={turnstileContainerRef} />
                                </div>

                                {submitError && (
                                    <p className="brd-reg-error">{submitError}</p>
                                )}
                                <button
                                    className="brd-reg-submit"
                                    onClick={submit}
                                    disabled={!ready || submitting}
                                    style={{ background: ready && !submitting ? "var(--color-primary)" : "#a9bbe0", cursor: ready && !submitting ? "pointer" : "not-allowed" }}
                                >
                                    {submitting ? "Registering…" : "Verify & Register"}
                                    {!submitting && (
                                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M7 17 17 7M8 7h9v9" />
                                        </svg>
                                    )}
                                </button>
                                <p className="brd-reg-privacy">Protected by Cloudflare — this check keeps bots from overloading the registry.</p>
                            </div>
                        )}

                        {view === "done" && (
                            <div className="brd-reg-done">
                                <span className="brd-reg-done-check">
                                    <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M20 6 9 17l-5-5" />
                                    </svg>
                                </span>
                                <h3 className="brd-reg-done-h2">You're in the registry</h3>
                                <p className="brd-reg-done-sub">
                                    Welcome, <strong>{doneName}</strong>. Your credentials are verified and you're now discoverable by contractors worldwide.
                                </p>
                                <div className="brd-reg-id-card">
                                    <span className="brd-reg-id-flag">{sel ? sel[1] : "🌍"}</span>
                                    <div className="brd-reg-id-info">
                                        <div className="brd-reg-id-label">ConRa Global Registry ID</div>
                                        <div className="brd-reg-id-value">{registryId}</div>
                                        <div className="brd-reg-id-sub">{sel ? sel[2] : ""} · {regVal}</div>
                                    </div>
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" /><path d="m9 12 2 2 4-4" />
                                    </svg>
                                </div>
                                <button
                                    className="brd-reg-submit"
                                    style={{ background: "var(--color-primary)", cursor: "pointer", marginBottom: 12 }}
                                    onClick={() => navigate(`/solutions/borderless/portfolio/${encodeURIComponent(regVal.trim())}`)}
                                >
                                    View my global portfolio
                                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M7 17 17 7M8 7h9v9" />
                                    </svg>
                                </button>
                                <button className="brd-reg-reset-btn" onClick={reset}>Register another architect</button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

const BorderlessPage = () => {
    const { hash } = useLocation();
    useEffect(() => {
        if (hash) {
            const el = document.querySelector(hash);
            if (el) el.scrollIntoView({ behavior: "smooth" });
        }
    }, [hash]);

    return (<div>
        <div className="web-only"><HeaderAny /></div>
        <div className="mobile-only"><MobileNav /></div>

        {/* ── Hero ── */}
        <section className="brd-hero">
            <div className="brd-hero-inner">
                <div className="brd-hero-text">
                    <h1 className="brd-hero-h1">
                        Borderless<br /><span className="brd-highlight">Architecture &amp; Engineering</span>
                    </h1>
                    <span className="brd-hero-rule" />
                    <p className="brd-hero-sub">
                        Architects are at the center of human space. Your vision defines how people live,
                        work and interact. ConRa was built to protect that creative vision and prove exactly
                        how it comes to life on the ground.
                    </p>
                    <blockquote className="brd-hero-quote">
                        <span className="brd-hero-quote-mark">&ldquo;</span>
                        <p>We shape our buildings; thereafter they shape us.</p>
                        <cite>WINSTON CHURCHILL</cite>
                    </blockquote>
                </div>
                <div className="brd-hero-visual">
                    <span className="brd-hero-ellipse" aria-hidden="true" />
                    <img src={heroCurveImg} alt="Curved modern architecture viewed from below" className="brd-hero-img" />
                </div>
            </div>
        </section>


        {/* ── Architect Registry ── */}
        <ArchitectRegistrySection />

        {/* ── Your ConRa Architect Profile ── */}
        <section className="brd-profile-section">
            <h2 className="brd-profile-heading">
                Your ConRa <span className="brd-highlight">Architect Profile</span>
            </h2>
            <div className="brd-profile-inner">
                <div className="brd-profile-text">
                    <h3 className="brd-profile-subhead">A Profile That<br />Proves Everything</h3>
                    <ul className="brd-proves-list">
                        {PROFILE_FEATURES.map(({ Icon, title, desc }) => (
                            <li key={title} className="brd-proves-item">
                                <span className="brd-proves-icon"><Icon /></span>
                                <div>
                                    <span className="brd-proves-item-title">{title}</span>
                                    <span className="brd-proves-item-desc">{desc}</span>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* ── Profile card ── */}
                <div className="brd-profile-card">
                    <div className="brd-profile-card-top">
                        <div className="brd-profile-avatar">JD</div>
                        <div className="brd-profile-info">
                            <p className="brd-profile-name">Arch. John Doe</p>
                            <p className="brd-profile-reg">Architect &nbsp;&middot;&nbsp; Reg. A2291</p>
                        </div>
                        <FaCheckCircle className="brd-profile-verified" />
                    </div>
                    <div className="brd-profile-card-body">
                        <div className="brd-profile-stats">
                            {PROFILE_STATS.map(({ value, label }) => (
                                <div key={label} className="brd-profile-stat">
                                    <span className="brd-stat-value">{value}</span>
                                    <span className="brd-stat-label">{label}</span>
                                </div>
                            ))}
                        </div>
                        <ul className="brd-profile-project-list">
                            {PROFILE_PROJECTS.map(({ name, code }) => (
                                <li key={code}>
                                    <span className="brd-profile-project-dot" />
                                    <span className="brd-profile-project-name">{name}</span>
                                    <span className="brd-profile-project-code">{code}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </section>

        {/* ── Protect What You Design ── */}
        <section className="brd-protect">
            <div className="brd-protect-inner">
                <div className="brd-protect-text">
                    <p className="brd-section-overtitle">PROTECT WHAT YOU DESIGN</p>
                    <h2 className="brd-protect-h2">Your Buildings Speak for You</h2>
                    <p className="brd-protect-sub">
                        A verified architectural portfolio that proves your design intent, showcases every
                        milestone and build credibility across borders.
                    </p>
                    <ul className="brd-protect-list">
                        {PROVES_CHECKLIST.map((text) => (
                            <li key={text}>
                                <FaCheckCircle className="brd-protect-check" />
                                <span>{text}</span>
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="brd-protect-visual">
                    <img src={buildingImg} alt="Verified building" className="brd-protect-img" />
                </div>
            </div>
        </section>

        {/* ── The Gap No Platform Has Closed ── */}
        <section className="brd-gap">
            <div className="brd-gap-inner">
                <h2 className="brd-gap-h2">The Gap No Platform Has Closed - Until Now</h2>
                <a className="brd-gap-link" href={GET_STARTED_URL} target="_blank" rel="noreferrer">
                    See sales solutions <FaArrowRight style={{ marginLeft: 6 }} />
                </a>
                <div className="brd-gap-grid">
                    <div className="brd-gap-col brd-gap-before">
                        <h3 className="brd-gap-col-title">Every Portfolio Before ConRa</h3>
                        <ul className="brd-gap-list">
                            {BEFORE_ITEMS.map(({ Icon, text }, i) => (
                                <li key={i} className="brd-gap-item">
                                    <span className="brd-gap-icon"><Icon /></span>
                                    <span>{text}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="brd-gap-divider" aria-hidden="true">
                        <FaArrowRight /><FaArrowRight className="brd-gap-divider-back" />
                    </div>
                    <div className="brd-gap-col brd-gap-after">
                        <h3 className="brd-gap-col-title brd-gap-col-title--after">A ConRa Architect Profile</h3>
                        <ul className="brd-gap-list">
                            {AFTER_ITEMS.map(({ Icon, text }, i) => (
                                <li key={i} className="brd-gap-item">
                                    <span className="brd-gap-icon"><Icon /></span>
                                    <span>{text}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </section>

        {/* ── Borderless Opportunities ── */}
        <section className="brd-global">
            <div className="brd-global-inner">
                <div className="brd-global-top">
                    <div className="brd-global-heading">
                        <p className="brd-global-overtitle">Borderless Opportunities</p>
                        <h2 className="brd-global-h2">Start with Verified<br />Credibility</h2>
                        <p className="brd-global-sub">
                            Your design history is your passport to international markets — your
                            authenticated data that speaks for you.
                        </p>
                    </div>
                    {/* <div className="brd-global-stats">
                        {GLOBAL_STATS.map(({ value, label }) => (
                            <div key={label} className="brd-global-stat">
                                <span className="brd-global-stat-value">{value}</span>
                                <span className="brd-global-stat-label">{label}</span>
                            </div>
                        ))}
                    </div> */}
                </div>

                <div className="brd-global-banner">
                    <img src={searchHeroImg} alt="ConRa global search and verification network" />
                </div>

            </div>
        </section>


        {/* ── Quote ── */}
        <section className="brd-quote">
            <img src={logoMark} alt="" className="brd-quote-logo" />
            <p className="brd-quote-text">
                &ldquo;Every great work done on a project goes to showcase the proficiency and creativity
                of architects and their importance in the built environment.&rdquo;
            </p>
            <cite className="brd-quote-cite">ConRa</cite>
        </section>

        {/* ── CTA ── */}
        <section className="brd-cta">
            <div className="brd-cta-inner">
                <h2 className="brd-cta-h2">
                    Start Building <span>Your Verified</span> Legacy Today
                </h2>
                <p className="brd-cta-sub">
                    Join thousands of construction professionals creating a legacy that outlives
                    resumes and PDF portfolios.
                </p>
                {/* <button className="filled-button" onClick={() => window.open(GET_STARTED_URL, "_blank")}>
                    Get Started <FaArrowRight style={{ marginLeft: 6 }} />
                </button> */}
            </div>
        </section>

        <Footer />
    </div>);
};

export default BorderlessPage;
